import { z } from 'zod';
import { Sha256, Uuid } from './common.ts';

/**
 * Contrato DesktopBridge v1: costura entre la interfaz (A) y el runtime nativo (B).
 * La UI nunca envía comandos ni programas; solo solicitudes tipadas.
 */
export const BRIDGE_PROTOCOL_VERSION = '1.0.0' as const;

export const LocalCapability = z.enum(['index', 'probe', 'transcribe', 'align', 'render', 'export_capcut']);
export type LocalCapability = z.infer<typeof LocalCapability>;
export const CapabilityState = z.enum(['available', 'missing_package', 'incompatible', 'unsupported']);
export type CapabilityState = z.infer<typeof CapabilityState>;

export const CapabilityReport = z.object({
  protocol_version: z.string().min(1).max(20),
  runtime: z.enum(['browser', 'desktop', 'mock']),
  engine: z.object({ available: z.boolean(), version: z.string().max(40).nullable() }),
  capabilities: z.record(LocalCapability, CapabilityState),
  transcription: z.object({ device: z.enum(['cpu', 'cuda']), compute_type: z.string().max(40),
    fallback_reason: z.enum(['GPU_MEMORY_UNAVAILABLE', 'GPU_RUNTIME_UNAVAILABLE']).nullable() }).nullable().optional(),
  /** Etiqueta visible cuando los datos no son reales. */
  simulated: z.boolean(),
});
export type CapabilityReport = z.infer<typeof CapabilityReport>;

export const RootDescriptor = z.object({
  id: Uuid,
  label: z
    .string()
    .min(1)
    .max(120)
    .refine((l) => !/^([A-Za-z]:[\\/]|\/|\\\\)/.test(l), 'La etiqueta no puede ser una ruta absoluta'),
  permissions: z.array(z.enum(['read', 'write'])).min(1),
});
export type RootDescriptor = z.infer<typeof RootDescriptor>;

export const IndexRequest = z.object({ root_id: Uuid, mode: z.enum(['incremental', 'full']) });
export type IndexRequest = z.infer<typeof IndexRequest>;

export const AssetResolutionRequest = z.object({ asset_version_id: Uuid, sha256: Sha256.nullable() });
export type AssetResolutionRequest = z.infer<typeof AssetResolutionRequest>;

/** Nunca un booleano optimista: el estado distingue ausente, modificado y revocado. */
export const AssetAvailabilityState = z.enum(['local_available', 'remote_authorized', 'absent', 'modified', 'permission_revoked', 'unknown']);
export const AssetAvailability = z.object({
  asset_version_id: Uuid,
  state: AssetAvailabilityState,
  checked_at: z.string().nullable(),
  device_id: Uuid.nullable(),
  reason: z.string().max(300).optional(),
});
export type AssetAvailability = z.infer<typeof AssetAvailability>;

export const MediaSessionRequest = z.object({ asset_version_id: Uuid, purpose: z.enum(['preview', 'review']) });
export type MediaSessionRequest = z.infer<typeof MediaSessionRequest>;

export const MediaSession = z.object({
  session_id: z.string().min(8).max(200),
  /** URL opaca y efímera restringida al recurso. Nunca file:// arbitrario. */
  url: z
    .string()
    .max(2000)
    .refine((u) => !/^file:/i.test(u), 'No se admiten URLs file://'),
  expires_at: z.string(),
  asset_version_id: Uuid,
});
export type MediaSession = z.infer<typeof MediaSession>;

export const LocalJobType = z.enum(['index', 'probe', 'transcribe', 'align', 'render_preview', 'render_final', 'export_capcut', 'analyze_audio']);

export const LocalJobRequest = z.object({
  job_id: Uuid,
  revision_id: Uuid.nullable(),
  type: LocalJobType,
  inputs_contract_version: z.literal(1),
  inputs: z.record(z.string(), z.unknown()),
  expected_artifacts: z.array(z.string().min(1).max(60)).max(50),
});
export type LocalJobRequest = z.infer<typeof LocalJobRequest>;

export const LocalJobRef = z.object({ job_id: Uuid, local_ref: z.string().min(1).max(200), simulated: z.boolean() });
export type LocalJobRef = z.infer<typeof LocalJobRef>;

/** Progreso de la etapa en curso; no se inventa un porcentaje global. */
export const DesktopEvent = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('job_progress'),
    seq: z.int().min(0),
    job_id: Uuid,
    attempt_generation: z.int().min(0),
    stage: z.string().max(80),
    stage_progress: z.number().min(0).max(1).nullable(),
    simulated: z.boolean(),
  }),
  z.object({
    type: z.literal('job_state'),
    seq: z.int().min(0),
    job_id: Uuid,
    attempt_generation: z.int().min(0),
    state: z.enum(['queued', 'running', 'completed', 'failed', 'cancelled']),
    error: z.object({ code: z.string().max(60), message: z.string().max(500) }).optional(),
    simulated: z.boolean(),
  }),
  z.object({ type: z.literal('capabilities_changed'), seq: z.int().min(0), report: CapabilityReport }),
  z.object({
    type: z.literal('index_progress'),
    seq: z.int().min(0),
    root_id: Uuid,
    files_seen: z.int().min(0),
    files_indexed: z.int().min(0),
    simulated: z.boolean(),
  }),
]);
export type DesktopEvent = z.infer<typeof DesktopEvent>;
